import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { formatFeedbackText } from "../../utils/feedbackFormatter";
import nexticn from "../../Components/Assets/svg/chevron-right-solid.svg";
import backicn from "../../Components/Assets/svg/back-chevron-right-solid (1).svg";
// Platform icons
import x from "../../Components/Assets/Management/x-task.png";
import fb from "../../Components/Assets/Management/fb-task.png";
import li from "../../Components/Assets/Management/linkedin-task.png";
import insta from "../../Components/Assets/Management/instagram-task.png";
import mail from "../../Components/Assets/Management/email-icn.png";
import utube from "../../Components/Assets/Management/youtube.png";

// Helper function to format dates for display
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

/**
 * Pending Task Section - displays and manages tasks awaiting approval
 */
export function PendingTaskSection({
  companyProfile,
  showPendingTasksSection,
  showPendingTaskDetail,
  onTaskStatusChange,
}) {
  const navigate = useNavigate();
  const pendingTasksScrollRef = useRef(null);

  // Component state
  const [pendingTasks, setPendingTasks] = useState([]); // Pending tasks from API
  const [selectedPendingTask, setSelectedPendingTask] = useState(null); // Selected task for detail view
  const [hasPendingTasks, setHasPendingTasks] = useState(false); // Tasks display flag
  const [showRollbackFeedbackForm, setShowRollbackFeedbackForm] =
    useState(false); // Rollback form visibility
  const [rollbackFeedbackText, setRollbackFeedbackText] = useState(""); // Rollback feedback text
  const [submitStatus, setSubmitStatus] = useState("idle"); // Submit state tracking
  const [buttonAction, setButtonAction] = useState(null); // Active button action
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger for refreshing tasks

  // Add new state to track if we're waiting for a new image after rollback
  const [waitingForNewImage, setWaitingForNewImage] = useState(false);
  const [lastImageUrl, setLastImageUrl] = useState(null);

  // Utility functions
  const getStatusClass = (status) => {
    switch (status) {
      case "Approved":
        return { backgroundColor: "#5eb35eb7" };
      case "Pending":
        return { backgroundColor: "rgba(255, 145, 0, 0.69)" };
      case "Roll Back":
        return { backgroundColor: "rgba(245, 65, 0, 0.69)" };
      case "Posted":
        return { backgroundColor: "rgba(172, 5, 255, 0.76)" };
      default:
        return {};
    }
  };

  // Format recurrence pattern for display
  const formatRecurrencePattern = (recurrence) => {
    if (!recurrence) return "";

    if (recurrence.recurrenceType === "monthly") {
      const week = recurrence.monthlyWeek || "first";
      const dayOfWeek = recurrence.monthlyDayOfWeek || "monday";

      const formattedDay =
        dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      const formattedWeek =
        week === "first"
          ? "1st"
          : week === "second"
          ? "2nd"
          : week === "third"
          ? "3rd"
          : week === "fourth"
          ? "4th"
          : "last";

      return `Every month on the ${formattedWeek} ${formattedDay}`;
    } else if (recurrence.recurrenceType === "weekly") {
      const dayOfWeek = recurrence.weeklyDayOfWeek || "monday";
      const formattedDay =
        dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      return `Every ${formattedDay}`;
    } else if (recurrence.recurrenceType === "bi-weekly") {
      const dayOfWeek = recurrence.weeklyDayOfWeek || "monday";
      const formattedDay =
        dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      return `Every other ${formattedDay}`;
    }

    return recurrence.recurrenceType || "";
  };

  // Fetch pending tasks from API
  const fetchPendingTasks = async () => {
    try {
      if (!companyProfile?._id) {
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please log in again", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/task/tasks/${companyProfile._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Filter tasks that are not "Approved" or "Posted" (these are pending tasks)
      const filteredPendingTasks = response.data.filter(
        (task) =>
          task.currentStatus !== "Approved" && task.currentStatus !== "Posted"
      );
      setPendingTasks(filteredPendingTasks);

      // Update whether there are tasks to display
      setHasPendingTasks(filteredPendingTasks.length > 0);
    } catch (error) {
      console.error("Error fetching pending tasks:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
      } else {
        toast.error("Error loading pending tasks", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
      }
    }
  };

  // Task status handler - add code to check for new images when task is refreshed
  const taskStatus = async (taskId) => {
    try {
      if (taskId === false) {
        // Reset state when closing task details
        setSelectedPendingTask(null);
        setWaitingForNewImage(false);
        setLastImageUrl(null);
        if (onTaskStatusChange) {
          onTaskStatusChange(false);
        }
        await fetchPendingTasks();
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        navigate("/login");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/task/${taskId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.data) {
        toast.error("No task data received", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        return;
      }

      const newTaskData = response.data;
      
      // Only do the check for enabling approve if we're waiting for new image
      if (waitingForNewImage && lastImageUrl && newTaskData.doneTaskImage) {
        if (newTaskData.doneTaskImage !== lastImageUrl) {
          console.log("New image detected!", {
            old: lastImageUrl.substring(0, 30),
            new: newTaskData.doneTaskImage.substring(0, 30)
          });
          setWaitingForNewImage(false);
          setLastImageUrl(null);
        }
      }
      
      setSelectedPendingTask(newTaskData);
      if (onTaskStatusChange) {
        onTaskStatusChange(newTaskData);
      }
      
      // ...rest of the existing code...
    } catch (error) {
      console.error("Error fetching task:", error);
      toast.error("Error loading task details", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
    }
  };

  // Global refresh notifier
  const dispatchTasksUpdated = () => {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      try { window.dispatchEvent(new CustomEvent('hussme:tasks-updated')); } catch (_) { try { window.dispatchEvent(new Event('hussme:tasks-updated')); } catch (_) {} }
    }
  };

  // Delete task handler
  const handleDeleteTask = async (taskId) => {
    const result = await Swal.fire({
      title: "Are you sure you want to Remove this task?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/task/remove-task/${taskId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Task deleted successfully", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });

        dispatchTasksUpdated();
        closeTaskDetailAndRefresh();
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(error.response?.data?.message || "Error deleting task", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
    }
  };

  // Approve task handler
  const handleApprove = async () => {
    try {
      if (!selectedPendingTask?._id) {
        toast.error("No task selected", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        return;
      }

      // Proactively notify
      dispatchTasksUpdated();

      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/task/update-task/${selectedPendingTask._id}`,
        { currentStatus: "Approved" },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      if (response.data) {
        toast.success("Task approved successfully", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        setWaitingForNewImage(false);
        setLastImageUrl(null);

        // Confirm success refresh
        dispatchTasksUpdated();

        closeTaskDetailAndRefresh();
      }
    } catch (error) {
      console.error("Error approving task:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Error approving task", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
    }
  };

  // Effect to fetch tasks when company profile changes
  useEffect(() => {
    if (companyProfile?._id) {
      fetchPendingTasks();
    }
  }, [companyProfile]);

  // Effect to refresh tasks when actions are performed
  useEffect(() => {
    if (refreshTrigger > 0 && companyProfile?._id) {
      fetchPendingTasks();
    }
  }, [refreshTrigger, companyProfile]);

  // Refetch on global task update events
  useEffect(() => {
    const onTasksUpdated = () => {
      if (companyProfile?._id) {
        fetchPendingTasks();
      }
    };
    window.addEventListener('hussme:tasks-updated', onTasksUpdated);
    return () => window.removeEventListener('hussme:tasks-updated', onTasksUpdated);
  }, [companyProfile?._id]);

  // Scroll functions for pending tasks horizontal scrolling
  const scrollPendingTasksLeft = () => {
    if (pendingTasksScrollRef.current) {
      pendingTasksScrollRef.current.scrollBy({
        left: -350,
        behavior: "smooth",
      });
    }
  };

  const scrollPendingTasksRight = () => {
    if (pendingTasksScrollRef.current) {
      pendingTasksScrollRef.current.scrollBy({
        left: 350,
        behavior: "smooth",
      });
    }
  };

  // Helper function to close task detail view and trigger refresh
  const closeTaskDetailAndRefresh = () => {
    onTaskStatusChange(false);
    setRefreshTrigger((prev) => prev + 1);
    // Reset form states
    setShowRollbackFeedbackForm(false);
    setRollbackFeedbackText("");
    setButtonAction(null);
    setSubmitStatus("idle");
  };

  // For in-task row rollback feedback
  const showFeedbackForm = () => {
    if (showRollbackFeedbackForm) {
      // Just closing the form
      setShowRollbackFeedbackForm(false);
      setButtonAction(null);
    } else {
      // Opening the form
      setShowRollbackFeedbackForm(true);
      setButtonAction("disapprove");
    }
  };

  const handleRollbackSubmit = async () => {
    if (!selectedPendingTask?._id) {
      toast.error("No task selected", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      return;
    }
    if (!rollbackFeedbackText.trim()) {
      toast.error("Please provide feedback before rolling back", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      return;
    }

    const token = localStorage.getItem("token");
    try {
      setSubmitStatus("submitting");

      // Proactively notify others
      dispatchTasksUpdated();

      // Prepare form data
      const formData = new FormData();
      formData.append("feedback", rollbackFeedbackText.trim());
      if (selectedPendingTask?.doneTaskImage) {
        formData.append("doneTaskImageUrl", selectedPendingTask.doneTaskImage);
      }
      const fileInput = document.getElementById("clientSuggestImage");
      if (fileInput && fileInput.files[0]) {
        formData.append("clientSuggestImage", fileInput.files[0]);
      }
      await axios.put(
        `${process.env.REACT_APP_API_URL}/task/add-feedback-image/${selectedPendingTask._id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await axios.put(
        `${process.env.REACT_APP_API_URL}/task/update-task/${selectedPendingTask._id}`,
        { currentStatus: "Roll Back" },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      console.log("Setting waitingForNewImage=true, lastImageUrl=",
        selectedPendingTask.doneTaskImage?.substring(0, 30));

      setWaitingForNewImage(true);
      setLastImageUrl(selectedPendingTask.doneTaskImage || null);
      toast.success("Task rolled back with feedback", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      setRollbackFeedbackText("");
      setButtonAction("approve");
      setShowRollbackFeedbackForm(false);

      // Confirm success refresh
      dispatchTasksUpdated();

      closeTaskDetailAndRefresh();
      setSubmitStatus("submitted");
      setTimeout(() => setSubmitStatus("idle"), 1500);
    } catch (error) {
      console.error("Error rolling back task:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Error rolling back task", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      setSubmitStatus("idle");
    }
  };

  // Update isTaskApprovable to handle missing doneTaskImage
  const isTaskApprovable = (task) => {
    // No task? Can't approve.
    if (!task) return false;
    
    // No done task image? Can't approve.
    if (!task.doneTaskImage || task.doneTaskImage.trim() === "") return false;
    
    // Feedback form is open? Can't approve.
    if (showRollbackFeedbackForm) return false;
    
    // If we're waiting for a new image after rollback
    if (waitingForNewImage && lastImageUrl) {
      // We can approve only if the image URL changed from what we recorded
      return task.doneTaskImage && task.doneTaskImage !== lastImageUrl;
    }
    
    // In all other cases, approve button is enabled
    return true;
  };

  // Add this helper function with the other utility functions
  const isTaskReferrable = (task) => {
    // Can only refer back tasks that have an image
    if (!task || !task.doneTaskImage || task.doneTaskImage.trim() === "") {
      return false;
    }
    
    // If we're waiting for a new image after rollback, disable refer back button
    if (waitingForNewImage && lastImageUrl) {
      return false;
    }
    
    return true;
  };

  return (
    <>
      {showPendingTasksSection && (
        <div className="task-arrows">
          <img
            src={backicn}
            alt=""
            className="assign-task-btn back"
            onClick={pendingTasks.length > 4 ? scrollPendingTasksLeft : undefined}
            style={{
              visibility: pendingTasks.length > 4 ? 'visible' : 'hidden',
              pointerEvents: pendingTasks.length > 4 ? 'auto' : 'none'
            }}
          />
          {hasPendingTasks ? (
            <>
              <div className="task-row-container" ref={pendingTasksScrollRef}>
                {pendingTasks.map((task) => (
                  <div className="box" key={task._id}>
                    <div
                      className="cart-task-box-wrapper"
                      onClick={() => taskStatus(task._id)}
                    >
                      <div className="cart-task-box">
                        <div className="top-id-card">
                          <div className="name-task">Task id:</div>
                          <div className="task-number">#{task.taskId}</div>
                        </div>
                        <div className="bottom-title-card">
                          <div className="task-box-title">{task.title}</div>
                          <button
                            className="cart-task-btn"
                            onClick={() => taskStatus(task._id)}
                          >
                            <span>View</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="temp-row-container tsk">
              <div className="temp-title">Click above to add your task!</div>
              <div className="temp-img"> </div>
            </div>
          )}
          <img
            src={nexticn}
            alt=""
            className="assign-task-btn next"
            onClick={pendingTasks.length > 4 ? scrollPendingTasksRight : undefined}
            style={{
              visibility: pendingTasks.length > 4 ? 'visible' : 'hidden',
              pointerEvents: pendingTasks.length > 4 ? 'auto' : 'none'
            }}
          />
        </div>
      )}

      {showPendingTaskDetail && (
        <div className="show-task-status-container">
          <div className="close-button-row">
            <img
              onClick={() => closeTaskDetailAndRefresh()}
              width="48"
              height="48"
              src="https://img.icons8.com/fluency/48/delete-sign.png"
              alt="delete-sign"
              className="status-cross-symbol"
            />
          </div>
          <div className="status-top-floor">
            <div className="status-top-floor-row-1">
              <div className="status-col-1">
                <div className="status-row client-padding">
                  <div className="status-task-id">
                    <div className="task-id for-gradient-font">Task ID</div>
                    <div className="task-id-number">
                      #{selectedPendingTask?.taskId}
                    </div>
                  </div>
                  <div
                    className="task-current-status"
                    style={getStatusClass(selectedPendingTask.currentStatus)}
                  >
                    {selectedPendingTask.currentStatus}
                  </div>
                  <div className="status-task-title">
                    <div className="task-title-status">Task Title</div>
                    <div className="task-title-name-status">
                      {selectedPendingTask?.title}
                    </div>
                  </div>
                  {selectedPendingTask.taskpurpose && (
                    <div className="status-task-title">
                      <div className="task-title-status">Task Objective</div>
                      <div className="task-title-name-status">
                        {selectedPendingTask?.taskpurpose}
                      </div>
                    </div>
                  )}
                  {selectedPendingTask.headline && (
                    <div className="status-task-title">
                      <div className="task-title-status">Main Headline</div>
                      <div className="task-title-name-status">
                        {selectedPendingTask?.headline}
                      </div>
                    </div>
                  )}
                  {selectedPendingTask.calltoaction && (
                    <div className="status-task-title">
                      <div className="task-title-status">Tagline for Lead</div>
                      <div className="task-title-name-status">
                        {selectedPendingTask?.calltoaction}
                      </div>
                    </div>
                  )}
                  {selectedPendingTask.description && (
                    <div className="status-task-des">
                      <div className="task-title-status">Description</div>
                      <div className="task-title-name-status">
                        {selectedPendingTask?.description}
                      </div>
                    </div>
                  )}

                  {/* Recurrence Type */}
                  {selectedPendingTask?.recurrence?.isRecurring && (
                    <div className="for-date-row">
                      <div className="status-task-dates status-recurring-type">
                        <label className="task-title-status">
                          Recurrence Type
                        </label>
                        <div className="status-target-date recurring-type-div">
                          <div className="task-title-name-status">
                            {formatRecurrencePattern(
                              selectedPendingTask.recurrence
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Platform Selection */}
                  {(selectedPendingTask.platform_x ||
                    selectedPendingTask.platform_fb ||
                    selectedPendingTask.platform_insta ||
                    selectedPendingTask.platform_li ||
                    selectedPendingTask.platform_youtube ||
                    selectedPendingTask.platform_via_mail) && (
                    <div className="platform-row-response">
                      <label className="task-title-status">
                        Selected Platforms has to Post:
                      </label>
                      <div className="social-checkbox">
                        {selectedPendingTask.platform_x && (
                          <img src={x} className="checkbox__textwrapper" />
                        )}
                        {selectedPendingTask.platform_fb && (
                          <img src={fb} className="checkbox__textwrapper" />
                        )}
                        {selectedPendingTask.platform_insta && (
                          <img src={insta} className="checkbox__textwrapper" />
                        )}
                        {selectedPendingTask.platform_li && (
                          <img src={li} className="checkbox__textwrapper" />
                        )}
                        {selectedPendingTask.platform_youtube && (
                          <img src={utube} className="checkbox__textwrapper" />
                        )}
                        {selectedPendingTask.platform_via_mail && (
                          <img src={mail} className="checkbox__textwrapper" />
                        )}
                      </div>
                    </div>
                  )}

                  {/* First row: Always show Submission Date and Target Posting Date */}
                  <div className="for-date-row">
                    <div className="status-task-dates">
                      <label className="task-title-status">
                        Submission Date
                      </label>
                      <div className="status-deadline-date">
                        <input
                          type="date"
                          value={formatDateForDisplay(
                            selectedPendingTask?.submissionForReview
                          )}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="status-task-dates">
                      <label className="task-title-status">
                        Target Posting Date
                      </label>
                      <div className="status-target-date">
                        <input
                          type="date"
                          name="targetPostingDate"
                          value={formatDateForDisplay(
                            selectedPendingTask?.targetPostingDate
                          )}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {/* Second row: Show recurrence dates only if task is recurring */}
                  {selectedPendingTask?.recurrence?.isRecurring && (
                    <div className="for-date-row">
                      <div className="status-task-dates">
                        <label className="task-title-status">
                          Recurrence Start Date
                        </label>
                        <div className="status-deadline-date">
                          <input
                            type="date"
                            value={formatDateForDisplay(
                              selectedPendingTask?.recurrence
                                ?.recurrenceStartDate
                            )}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="status-task-dates">
                        <label className="task-title-status">
                          Recurrence End Date
                        </label>
                        <div className="status-deadline-date">
                          <input
                            type="date"
                            value={formatDateForDisplay(
                              selectedPendingTask?.recurrence?.recurrenceEndDate
                            )}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Reference Image */}
                  {selectedPendingTask.referenceImage && (
                    <div className="info-group-img">
                      <label className="task-title-status">
                        Reference Image
                      </label>
                      <a
                        href={selectedPendingTask.referenceImage}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={selectedPendingTask.referenceImage}
                          alt="Brand Relate Image 6"
                          className="profile-logo"
                        />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Task Preview Column */}
              <div className="status-col-2">
                <div className="status-title-preview">Task Preview</div>
                <div className="task-preview-img-container">
                  <a
                    href={
                      selectedPendingTask?.doneTaskImage &&
                      selectedPendingTask.doneTaskImage.trim() !== ""
                        ? selectedPendingTask.doneTaskImage
                        : "https://productionhussmestorage.blob.core.windows.net/hussmeassets/Work in progress image.png"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open image in new tab"
                  >
                    <img
                      src={
                        selectedPendingTask?.doneTaskImage &&
                        selectedPendingTask.doneTaskImage.trim() !== ""
                          ? selectedPendingTask.doneTaskImage
                          : "https://productionhussmestorage.blob.core.windows.net/hussmeassets/Work in progress image.png"
                      }
                      className="task-preview-img"
                      alt="Task Preview"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons Column */}
          <div className="status-col-3">
            <div className="status-task-btns">
              {/* Rollback Feedback History */}
              {selectedPendingTask.rollbackFeedback.length > 0 && (
                <div className="past-rollback">
                  <h3 className="rollback-title">Rollback Feedback</h3>
                  <ul>
                    {selectedPendingTask.rollbackFeedback
                      .slice(-4)
                      .map((feedback, index, arr) => (
                        <li
                          key={
                            arr.length === 4
                              ? index +
                                selectedPendingTask.rollbackFeedback.length -
                                4
                              : index
                          }
                        >
                          {feedback.clientSuggestImage ? (
                            <div className="info-group-img">
                              <a
                                href={feedback.clientSuggestImage}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={feedback.clientSuggestImage}
                                  alt="Brand Relate Image 6"
                                  className="profile-logo"
                                />
                              </a>
                            </div>
                          ) : (
                            <div className="info-group-img">
                              <a
                                href={feedback.doneTaskImage}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={feedback.doneTaskImage}
                                  alt="Brand Relate Image 6"
                                  className="profile-logo"
                                />
                              </a>
                            </div>
                          )}
                          {/* Only render numbered feedback when there is non-empty text */}
                          {((feedback?.feedback ?? '').toString().trim().length > 0) && (
                            <div className="feed-index">
                              {(() => {
                                const txt = (feedback?.feedback ?? '').toString().trim();
                                const displayCount = arr.length;
                                const hasBuiltInNumbering = /^\d+[\.\)]/.test(txt);

                                // Only show numbering if there are multiple feedback items
                                if (displayCount > 1) {
                                  const n = selectedPendingTask.rollbackFeedback.length > 4
                                    ? selectedPendingTask.rollbackFeedback.length - 4 + index + 1
                                    : index + 1;
                                  return <div className="feedback-number">{n})</div>;
                                }
                                return null;
                              })()}
                              <div className="feedback-text">
                                {formatFeedbackText(feedback.feedback, `feedback-${index}`)}
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {/* Action Buttons - Always visible; hide Approve/Refer Back when not allowed */}
              <div className="row-3-col-2-btn">
                <button
                  className="custom-button-2 delete-task-btn"
                  onClick={() => handleDeleteTask(selectedPendingTask._id)}
                >
                  Remove Task
                </button>

                {isTaskApprovable(selectedPendingTask) && (
                  <button
                    className="custom-button-2 approve-task-btn"
                    onClick={handleApprove}
                    title="Approve this task"
                  >
                    <span className="button-content">
                      <span className="button-text">Approve</span>
                    </span>
                  </button>
                )}

                {isTaskReferrable(selectedPendingTask) && (
                  <button
                    className="custom-button-2 refer-back-btn"
                    onClick={() => showFeedbackForm()}
                    title="Provide feedback and refer task back to designer"
                  >
                    <span className="button-content">
                      <span className="button-text">Refer Back</span>
                    </span>
                  </button>
                )}

                {/* Feedback Form */}
                {showRollbackFeedbackForm && (
                  <div className="status-feedback-form">
                    <textarea
                      className="feedback-textarea"
                      placeholder="Leave your feedback"
                      value={rollbackFeedbackText}
                      onChange={(e) => setRollbackFeedbackText(e.target.value)}
                    ></textarea>
                    <div className="new-task-row-input">
                      <label htmlFor="clientSuggestImage">Suggestion:</label>
                      <input
                        type="file"
                        name="clientSuggestImage"
                        className="img-input"
                        accept="image/*"
                        id="clientSuggestImage"
                      />
                    </div>
                    <button
                      className="submit-feedback-btn"
                      disabled={
                        submitStatus === "submitting" ||
                        submitStatus === "submitted"
                      }
                      onClick={handleRollbackSubmit}
                    >
                      {submitStatus === "submitting"
                        ? "Submitting..."
                        : submitStatus === "submitted"
                        ? "Submitted"
                        : "Submit"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}